import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo_2.png";
import Container from "./Container";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setShowNavbar(false);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => setShowNavbar(true), 280);
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const KEY = "hsl_download_prompt_hidden_until";

    try {
      const hiddenUntil = Number(localStorage.getItem(KEY) || "0");
      const now = Date.now();
      if (now > hiddenUntil) {
        const t = setTimeout(() => setShowDownloadDialog(true), 900);
        return () => clearTimeout(t);
      }
    } catch {
      const t = setTimeout(() => setShowDownloadDialog(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const closeForToday = () => {
    const KEY = "hsl_download_prompt_hidden_until";
    const oneDay = 24 * 60 * 60 * 1000;
    try {
      localStorage.setItem(KEY, String(Date.now() + oneDay));
    } catch {}
    setShowDownloadDialog(false);
  };

  const closeOnly = () => setShowDownloadDialog(false);
  const APP_DOWNLOAD_URL = "https://huntsharelive.com/app-release.apk";

  const links = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "All Streams", path: "/streams" },
    { name: "Policy", path: "/policy" },
    { name: "Child Safety", path: "/child-safety" },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <>
      {isOpen && <div className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-sm md:hidden" onClick={() => setIsOpen(false)} />}

      <nav
        className={`fixed left-0 top-0 z-50 w-full transition-all duration-300 ${
          scrolled ? "bg-slate-950/80 shadow-[0_10px_40px_rgba(0,0,0,0.25)]" : "bg-transparent"
        } ${showNavbar ? "translate-y-0" : "-translate-y-full"}`}
      >
        <div className="border-b border-white/10 backdrop-blur-xl">
          <Container>
            <div className="flex h-16 items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src={logo} alt="Hunt Share Live" className="h-10 w-auto" />
                <span className="text-lg font-semibold tracking-wide text-white">Hunt Share Live</span>
              </Link>

              <div className="hidden items-center gap-7 md:flex">
                {links.map((link) => (
                  <Link key={link.path} to={link.path} className="text-sm font-medium text-slate-200 transition hover:text-orange-300">
                    {link.name}
                  </Link>
                ))}
              </div>

              <a href={APP_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="hidden rounded-full bg-gradient-to-r from-orange-500 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 md:inline-flex">
                Download App
              </a>

              <button onClick={() => setIsOpen(!isOpen)} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white md:hidden" aria-label="Toggle navigation">
                <span className="text-xl">☰</span>
              </button>
            </div>
          </Container>
        </div>

        <div className={`fixed right-0 top-0 z-40 h-full w-72 border-l border-white/10 bg-slate-950/95 p-6 backdrop-blur-xl transition-transform duration-300 md:hidden ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
          <div className="mt-12 flex flex-col gap-4">
            {links.map((link) => (
              <Link key={link.path} to={link.path} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium text-slate-200 transition hover:border-orange-300/50 hover:text-orange-300" onClick={() => setIsOpen(false)}>
                {link.name}
              </Link>
            ))}
            <a href={APP_DOWNLOAD_URL} target="_blank" rel="noreferrer" className="mt-4 rounded-full bg-gradient-to-r from-orange-500 to-fuchsia-600 px-4 py-3 text-center text-sm font-semibold text-white" onClick={() => setIsOpen(false)}>
              Download App
            </a>
          </div>
        </div>
      </nav>

      {showDownloadDialog && (
        <div className="fixed bottom-5 right-5 z-[60] max-w-sm rounded-[1.5rem] border border-white/10 bg-slate-900/90 p-5 text-white shadow-[0_25px_70px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <button className="absolute right-3 top-3 text-slate-400" onClick={closeOnly} aria-label="Close">✕</button>
          <div className="pr-6">
            <p className="text-lg font-semibold">Get the Hunt Share Live app</p>
            <p className="mt-2 text-sm text-slate-400">Faster streaming, better experience, and instant updates.</p>
          </div>
          <div className="mt-4 flex gap-3">
            <button className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300" onClick={closeForToday}>Not now</button>
            <a className="rounded-full bg-gradient-to-r from-orange-500 to-fuchsia-600 px-4 py-2 text-center text-sm font-semibold text-white" href={APP_DOWNLOAD_URL} target="_blank" rel="noreferrer" onClick={() => setShowDownloadDialog(false)}>
              Download
            </a>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;
